import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WITHOUT_NOTIFICATION_HEADER } from '@core/constants';
import {
  PaymentMethodModel,
  SingleItemResponse,
  ThreeDSType,
  TokenizePaymentMethodModel,
  TokenizedCardResponseModel,
  WithoutNotificationHeader,
} from '@core/models';
import { ThreeDSService } from '@core/services/3ds/3ds.service';
import { Api } from '@core/services/api.state';
import { ApiService } from '@core/services/api/api.service';
import { ExternalSessionState } from '@core/state/external';
import { environment } from '@rcsa-frontend/environments/environment';
import { Observable, map, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ExternalPaymentMethodsService extends Api<PaymentMethodModel> {
  override endpoint: string;

  constructor(
    protected override readonly apiService: ApiService<PaymentMethodModel>,
    private externalSessionState: ExternalSessionState,
    private http: HttpClient,
    private threeDSService: ThreeDSService
  ) {
    super(apiService);
    this.endpoint = `${environment.apiUrl}/v1/external/account/payment-methods`;
  }

  public createPaymentMethod(data: any): Observable<PaymentMethodModel> {
    return this.http
      .post<SingleItemResponse<PaymentMethodModel>>(this.endpoint, data)
      .pipe(
        switchMap((response) => {
          if (response.data.iframe) {
            return this.threeDSService.open3DS(
              ThreeDSType.ADDING_CARD,
              response.data,
              {
                baseUrl: `/v1/external/account/payment-methods/sse?payment_method_id=${response.data.id}`,
                token: this.externalSessionState.accessToken!,
              },
              (paymentId) =>
                this.find(paymentId, {
                  [WITHOUT_NOTIFICATION_HEADER]: WithoutNotificationHeader.ALL,
                })
            );
          }
          return of(response.data);
        })
      );
  }

  public createTokenizedPaymentMethod(
    data: PaymentMethodModel
  ): Observable<PaymentMethodModel> {
    const endpoint = `${environment.apiUrl}/v1/external/account/payment-methods/tokenize-save-card`;
    return this.http
      .post<SingleItemResponse<PaymentMethodModel>>(
        endpoint,
        JSON.stringify(data)
      )
      .pipe(
        switchMap((response) => {
          if (response.data.iframe) {
            return this.threeDSService.open3DS(
              ThreeDSType.ADDING_CARD,
              response.data,
              {
                baseUrl: `/v1/external/account/payment-methods/sse?payment_method_id=${response.data.id}`,
                token: this.externalSessionState.accessToken!,
              },
              (paymentId) =>
                this.find(paymentId, {
                  [WITHOUT_NOTIFICATION_HEADER]: WithoutNotificationHeader.ALL,
                })
            );
          }
          return of(response.data);
        })
      );
  }

  public deletePaymentMethod(data: {
    email: string;
    paymentMethodId: string;
  }): Observable<PaymentMethodModel> {
    const endpoint = `${environment.apiUrl}/v1/external/account/payment-methods/${data.paymentMethodId}`;
    return this.http
      .delete<SingleItemResponse<PaymentMethodModel>>(endpoint, {
        body: { email: data.email },
      })
      .pipe(
        map((response) => {
          return response.data;
        })
      );
  }

  public paymentMethods(): Observable<PaymentMethodModel[]> {
    return this.getAll().pipe(
      map((result) => {
        return result.data.items;
      })
    );
  }

  public saveUntokenizedPaymentMethod(
    data: TokenizePaymentMethodModel
  ): Observable<TokenizedCardResponseModel> {
    const endpoint = `${this.endpoint}/tokenize`;
    return this.http
      .post<SingleItemResponse<TokenizedCardResponseModel>>(endpoint, data)
      .pipe(
        map((result) => {
          return result.data;
        })
      );
  }
}
